import { useEffect } from 'react';

const PRODUCTION_ORIGIN = 'https://nobelly.in';
const DEFAULT_OG_IMAGE = `${PRODUCTION_ORIGIN}/og-default.jpg`;

/**
 * Sets per-page title, meta description, canonical URL and Open Graph / Twitter
 * copy. Falls back to sensible defaults so pages that don't call this still
 * inherit the site-wide values from index.html.
 *
 * @param {{title:string, description:string, path:string, ogImage?:string}} meta
 */
export default function usePageMeta({ title, description, path, ogImage }) {
  useEffect(() => {
    if (typeof document === 'undefined') return;

    if (title) document.title = title;

    const setMeta = (attr, key, value) => {
      if (!value) return;
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', value);
    };

    if (description) {
      setMeta('name', 'description', description);
      setMeta('property', 'og:description', description);
      setMeta('name', 'twitter:description', description);
    }
    if (title) {
      setMeta('property', 'og:title', title);
      setMeta('name', 'twitter:title', title);
    }
    setMeta('property', 'og:image', ogImage || DEFAULT_OG_IMAGE);
    setMeta('name', 'twitter:image', ogImage || DEFAULT_OG_IMAGE);
    setMeta('property', 'og:url', `${PRODUCTION_ORIGIN}${path || '/'}`);

    // Canonical: always points at production domain, regardless of preview host.
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${PRODUCTION_ORIGIN}${path || '/'}`);
  }, [title, description, path, ogImage]);
}
